import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { ReactNode } from "react";
import { useIntl } from "react-intl";
import { Navigate, useParams } from "react-router-dom";
import { formatDateOnly } from "utils/dateUtils";

type DetailRow = { label: string; node: ReactNode };

const RequirementDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const {
    requirementService,
    licensesUrl,
    licenseDetailsUrl,
  } = useGlobalContext();

  const requirementResult = useStrictQuery(
    ["requirementService.getRequirementById", id],
    () => requirementService.getRequirementById(id!),
    {
      enabled: !!id,
    },
  );

  const requirement = requirementResult.data?.data;
  useTitle(requirement?.name || "Requirement Details");

  if (!id) return <Navigate to={licensesUrl()} replace />;
  if (requirementResult.isLoading) return <Loader />;

  if (!requirement) {
    return (
      <>
        <RedirectBackButton
          redirectRoute={licensesUrl()}
          labelKey="backToListButtonLabel"
        />
        <Alert severity="info">{t({ id: "requirementNotFound" })}</Alert>
      </>
    );
  }

  const yesNoChip = (value: boolean) => (
    <Chip
      size="small"
      color={value ? "primary" : "default"}
      label={value ? t({ id: "yes" }) : t({ id: "no" })}
    />
  );

  const detailRows: DetailRow[] = [
    { label: t({ id: "nameLabel" }), node: requirement.name ?? "-" },
    {
      label: t({ id: "descriptionLabel" }),
      node: requirement.description ?? "-",
    },
    {
      label: t({ id: "ruleType" }),
      node: requirement.ruleTypeName ? (
        <Chip
          size="small"
          color={
            requirement.ruleTypeName === "Minimum" ? "primary" : "secondary"
          }
          label={requirement.ruleTypeName}
        />
      ) : (
        "-"
      ),
    },
    {
      label: t({ id: "measureType" }),
      node: requirement.measureTypeName ?? "-",
    },
    { label: t({ id: "amountLabel" }), node: requirement.amount ?? "-" },
    {
      label: t({ id: "recurrenceType" }),
      node: requirement.recurrenceTypeName ?? "-",
    },
    {
      label: t({ id: "recurrenceIntervalCycles" }),
      node: requirement.recurrenceIntervalCycles ?? "-",
    },
    {
      label: t({ id: "effectiveStartDate" }),
      node: formatDateOnly(requirement.effectiveStartDate) ?? "-",
    },
    {
      label: t({ id: "effectiveEndDate" }),
      node: formatDateOnly(requirement.effectiveEndDate) ?? "-",
    },
    {
      label: t({ id: "isAdditive" }),
      node: yesNoChip(!!requirement.isAdditive),
    },
    {
      label: t({ id: "isPerEarnPeriod" }),
      node: yesNoChip(!!requirement.isPerEarnPeriod),
    },
    { label: t({ id: "sortOrder" }), node: requirement.sortOrder ?? "-" },
  ];

  const constraintRows = (requirement.constraints ?? []).map((c, i) => ({
    ...c,
    id: c.id ?? String(i),
    constraintTypeName: c.constraintTypeName ?? "-",
    topicCategoryName: c.topicCategoryName ?? "-",
    formatTypeName: c.formatTypeName ?? "-",
    accreditorName: c.accreditorName ?? "-",
    audienceDesignator: c.audienceDesignator ?? "-",
  }));

  const constraintCells: Array<TableCellType> = [
    {
      accessor: "constraintTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "constraintType" }),
    },
    {
      accessor: "topicCategoryName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "subjectAreaLabel" }),
    },
    {
      accessor: "formatTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "formatLabel" }),
    },
    {
      accessor: "accreditorName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "accreditorLabel" }),
    },
    {
      accessor: "audienceDesignator",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "audienceLabel" }),
    },
    {
      accessor: "logicalGroup",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "logicalGroup" }),
    },
  ];

  const conditionRows = (requirement.applicabilityConditions ?? []).map(
    (c, i) => ({
      ...c,
      id: c.id ?? String(i),
      conditionTypeName: c.conditionTypeName ?? "-",
      operatorTypeName: c.operatorTypeName ?? "-",
      referenceValue: c.referenceValue ?? "-",
      rangeEndValue: c.rangeEndValue ?? "-",
    }),
  );

  const conditionCells: Array<TableCellType> = [
    {
      accessor: "conditionTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "conditionType" }),
    },
    {
      accessor: "operatorTypeName",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "operatorLabel" }),
    },
    {
      accessor: "referenceValue",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "referenceValue" }),
    },
    {
      accessor: "rangeEndValue",
      noSort: true,
      align: "left",
      type: "text",
      label: t({ id: "rangeEndValue" }),
    },
  ];

  return (
    <>
      <RedirectBackButton
        redirectRoute={licenseDetailsUrl(requirement.licenseId)}
        labelKey="backToLicense"
        labelValues={{ license: requirement.licenseDisplayName ?? "" }}
      />
      <h1>{requirement.name}</h1>

      <Typography variant="h5" sx={{ margin: "20px 0" }}>
        {t({ id: "requirementLabel" })}
      </Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {detailRows.map((row, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row" width="30%">
                  <b>{row.label}</b>
                </TableCell>
                <TableCell>{row.node}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Accordion sx={{ mt: 3 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "requirementConstraints" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {constraintRows.length > 0 ? (
            <SortableTable
              cells={constraintCells}
              rows={constraintRows}
              dense
              // Constraints in the same logical group are OR-ed, so they have to
              // stay grouped. Without this the table re-sorts them by their first
              // column and the grouping stops being readable.
              defaultOrderBy="logicalGroup"
            />
          ) : (
            <Alert severity="info">{t({ id: "noConstraints" })}</Alert>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t({ id: "applicabilityConditions" })}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {conditionRows.length > 0 ? (
            <SortableTable cells={conditionCells} rows={conditionRows} dense />
          ) : (
            <Alert severity="info">
              {t({ id: "noApplicabilityConditions" })}
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default RequirementDetails;
